import {
  Chip,
  Container,
  Grid,
  Link as LinkMui,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import BaseBreadcrumbs from "../components/BaseBreadcrumbs";
import Meta from "../components/Meta";
import Section from "../components/Section";
import { popularStyleIdeas } from "$/data/popular-style-ideas";

const parseTagsData = () => {
  return popularStyleIdeas.map((item, index) => ({
    id: index + 1,
    title: item.title,
    tag: item.title.replace(/\s+/g, "-"),
    url: item.url,
    list_url: item.list_url,
    clicks: 0, // Since clicks are not in the TS data
  }));
};

/**
 * Skeleton component for displaying loading state
 */
function TagSkeleton() {
  return (
    <>
      {Array.from(Array(10).keys()).map((i) => (
        <Grid item xs={12} key={i}>
          <Typography variant="h4" sx={{ marginBottom: 2 }}>
            <Skeleton variant="rounded" width={200} height={32} />
          </Typography>
          <Stack direction="row" spacing={0} sx={{ flexWrap: "wrap", gap: 1 }}>
            {Array.from(Array(i === 0 ? 10 : 30).keys()).map((j) => (
              <Skeleton key={j} variant="rounded" width={60} height={32} />
            ))}
          </Stack>
        </Grid>
      ))}
    </>
  );
}

/**
 * Main component for displaying the popular Styles sitemap
 */
function PopularStylesSitemapPage() {
  const location = useLocation();

  // Parse page from URL query parameter or default to 1
  const getPageFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get("page");
    return pageParam ? parseInt(pageParam, 10) : 1;
  };

  const [currentPage, setCurrentPage] = React.useState(getPageFromQuery());
  const itemsPerPage = 1000;

  // Parse and paginate CSV data
  const [isLoading, setIsLoading] = React.useState(true);
  const [tags, setTags] = React.useState([]);
  const [categoriesData, setCategoriesData] = React.useState(null);

  // Load and process CSV data
  React.useEffect(() => {
    const processData = () => {
      setIsLoading(true);
      const allTags = parseTagsData();

      // Calculate pagination
      const start = (currentPage - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      const paginatedTags = allTags.slice(start, end);

      setTags(paginatedTags);
      setIsLoading(false);
    };

    processData();
  }, [currentPage]);

  // Update page when URL query parameter changes
  React.useEffect(() => {
    const page = getPageFromQuery();
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  }, [location.search]);

  // Process tags data into categories when tags change
  React.useEffect(() => {
    if (tags && tags.length > 0) {
      // Group tags by category (using first letter as category)
      const categoriesObj = tags.reduce((r, a) => {
        const category = a.title[0].toUpperCase();
        r[category] = r[category] || [];
        r[category].push(a);
        return r;
      }, Object.create(null));
      setCategoriesData(categoriesObj);
    }
  }, [tags]);

  // Render category content
  const renderCategoryContent = () => {
    if (isLoading || !categoriesData) {
      return <TagSkeleton />;
    }

    return Object.keys(categoriesData)
      .sort()
      .map((cat) => (
        <Grid item xs={12} key={cat}>
          <Typography
            variant="h4"
            variantMapping={{ h4: "h3" }}
            sx={{ marginBottom: 2 }}
          >
            {`${cat} Tattoos`}
          </Typography>
          <Stack direction="row" spacing={0} sx={{ flexWrap: "wrap", gap: 1 }}>
            {categoriesData[cat].map((tag) => (
              <Chip
                label={tag.title}
                key={tag.id}
                component={Link}
                to={`${tag.url}`}
                clickable
              />
            ))}
          </Stack>
        </Grid>
      ));
  };

  return (
    <>
      <Meta
        title={`Popular Styles Sitemap`}
        description="Explore our most popular Styles to inspire you for your next
            tattoo. Unique tattoo designs sorted by popularity."
      />
      <Section>
        <Container>
          <BaseBreadcrumbs title="Popular Styles Sitemap" />
          <Typography
            variant="h3"
            variantMapping={{ h3: "h1" }}
            color="primary"
            sx={{ marginBottom: 1 }}
          >
            Popular Styles Sitemap
          </Typography>
          <Typography
            sx={{ marginBottom: 4 }}
            variant="body1"
            variantMapping={{ body1: "h2" }}
          >
            <LinkMui component={Link} to="/">
              <strong>Start creating</strong>
            </LinkMui>{" "}
            your own for free.
          </Typography>

          <Grid container spacing={5}>
            {renderCategoryContent()}
          </Grid>
        </Container>
      </Section>
    </>
  );
}

export default PopularStylesSitemapPage;
